import {
  SpirometryIcon,
  CardiopulmonaryIcon,
  ExertionRecoveryIcon,
  ManualAudioIcon,
  SixMinuteWalkIcon,
} from "../../components/icons/recordingModeIcons";

export const PARTICIPANT_ID_ENTRY_MODES = {
  freeEntry: "free_entry",
  allowlist: "allowlist",
};

export const PARTICIPANT_ID_FORMATS = {
  alphanumeric: "alphanumeric",
  numeric: "numeric",
};

export const RECORDING_MODES = [
  {
    value: "spirometry",
    labelKey: "studies.recordingModes.spirometry",
    labelDefault: "Spirometry",
    Icon: SpirometryIcon,
  },
  {
    value: "tidal-breathing",
    labelKey: "studies.recordingModes.cardiopulmonary",
    labelDefault: "Cardiopulmonary",
    Icon: CardiopulmonaryIcon,
  },
  {
    value: "post-exertion",
    labelKey: "studies.recordingModes.postExertion",
    labelDefault: "Exertion Recovery",
    Icon: ExertionRecoveryIcon,
  },
  {
    value: "six-minute-walk",
    labelKey: "studies.recordingModes.sixMinuteWalk",
    labelDefault: "6-Minute Walk",
    Icon: SixMinuteWalkIcon,
  },
  {
    value: "manual-audio",
    labelKey: "studies.recordingModes.manualAudio",
    labelDefault: "Manual Audio",
    Icon: ManualAudioIcon,
  },
];

export const DEFAULT_RECORDING_MODES = ["spirometry", "tidal-breathing"];

const PRODUCT_PRESENTATION_RECORDING_MODE_FEATURE_KEYS = Object.freeze({
  spirometry: "spirometry",
  "tidal-breathing": "cardiopulmonary",
  "post-exertion": "exertionRecovery",
  "six-minute-walk": "sixMinuteWalk",
  "manual-audio": "manualAudio",
});

export const STUDY_USER_WEB_APP_FEATURE_SECTIONS = [
  {
    sectionKey: "results",
    titleKey: "admin.createOrg.featureSections.results",
    titleFallback: "Results",
    features: [
      {
        featureKey: "rr",
        labelKey: "admin.createOrg.featureLabels.rr",
        labelFallback: "RR",
      },
      {
        featureKey: "hr",
        labelKey: "admin.createOrg.featureLabels.hr",
        labelFallback: "HR",
      },
      {
        featureKey: "hrv",
        labelKey: "admin.createOrg.featureLabels.hrv",
        labelFallback: "HRV",
      },
      {
        featureKey: "tv",
        labelKey: "admin.createOrg.featureLabels.tv",
        labelFallback: "TV",
      },
      {
        featureKey: "fev1",
        labelKey: "admin.createOrg.featureLabels.fev1",
        labelFallback: "FEV1",
      },
      {
        featureKey: "fvc",
        labelKey: "admin.createOrg.featureLabels.fvc",
        labelFallback: "FVC",
      },
      {
        featureKey: "pef",
        labelKey: "admin.createOrg.featureLabels.pef",
        labelFallback: "PEF",
      },
      {
        featureKey: "fev1Fvc",
        labelKey: "admin.createOrg.featureLabels.fev1Fvc",
        labelFallback: "FEV1/FVC",
      },
      {
        featureKey: "flowLoops",
        labelKey: "admin.createOrg.featureLabels.flowLoops",
        labelFallback: "Flow Loops",
      },
      {
        featureKey: "relativeChange",
        labelKey: "admin.createOrg.featureLabels.relativeChange",
        labelFallback: "Relative Change (%)",
      },
      {
        featureKey: "cardiacQuality",
        labelKey: "admin.createOrg.featureLabels.cardiacQuality",
        labelFallback: "Cardiac Quality",
      },
      {
        featureKey: "signalQuality",
        labelKey: "admin.createOrg.featureLabels.signalQuality",
        labelFallback: "Signal Quality",
      },
      {
        featureKey: "spirometryQuality",
        labelKey: "admin.createOrg.featureLabels.spirometryQuality",
        labelFallback: "Spirometry Quality",
      },
    ],
  },
  {
    sectionKey: "elements",
    titleKey: "admin.createOrg.featureSections.elements",
    titleFallback: "Elements",
    features: [
      {
        featureKey: "poincareChart",
        labelKey: "admin.createOrg.featureLabels.poincareChart",
        labelFallback: "Poincaré Chart",
      },
      {
        featureKey: "respiratorySignalChart",
        labelKey: "admin.createOrg.featureLabels.respiratorySignalChart",
        labelFallback: "Respiratory Signal Chart",
      },
      {
        featureKey: "spirometrySegmentChart",
        labelKey: "admin.createOrg.featureLabels.spirometrySegmentChart",
        labelFallback: "Spirometry Segment Chart",
      },
    ],
  },
  {
    sectionKey: "misc",
    titleKey: "admin.createOrg.featureSections.misc",
    titleFallback: "Miscellaneous",
    features: [
      {
        featureKey: "weatherDetails",
        labelKey: "admin.createOrg.featureLabels.weatherDetails",
        labelFallback: "Weather Details",
      },
    ],
  },
];

export const QUESTIONNAIRES = [
  {
    value: "cat",
    labelKey: "studies.questionnaires.cat",
    labelDefault: "CAT",
  },
  {
    value: "caat",
    labelKey: "studies.questionnaires.caat",
    labelDefault: "CAAT",
  },
  {
    value: "sgrq",
    labelKey: "studies.questionnaires.sgrq",
    labelDefault: "SGRQ",
  },
  {
    value: "r-scale",
    labelKey: "studies.questionnaires.rScale",
    labelDefault: "R-Scale",
  },
  {
    value: "act",
    labelKey: "studies.questionnaires.act",
    labelDefault: "ACT",
  },
  {
    value: "weekly-check-in",
    labelKey: "studies.questionnaires.weeklyCheckIn",
    labelDefault: "Weekly Check In",
  },
  {
    value: "monthly-check-in",
    labelKey: "studies.questionnaires.monthlyCheckIn",
    labelDefault: "Monthly Check In",
  },
];

export const QUESTIONNAIRE_FREQUENCIES = [
  {
    value: "daily",
    labelKey: "studies.questionnaires.frequency.daily",
    labelDefault: "Daily",
  },
  {
    value: "weekly",
    labelKey: "studies.questionnaires.frequency.weekly",
    labelDefault: "Weekly",
  },
  {
    value: "monthly",
    labelKey: "studies.questionnaires.frequency.monthly",
    labelDefault: "Monthly",
  },
  {
    value: "3_monthly",
    labelKey: "studies.questionnaires.frequency.3_monthly",
    labelDefault: "Every 3 months",
  },
  {
    value: "6_monthly",
    labelKey: "studies.questionnaires.frequency.6_monthly",
    labelDefault: "Every 6 months",
  },
  {
    value: "yearly",
    labelKey: "studies.questionnaires.frequency.yearly",
    labelDefault: "Yearly",
  },
];

export const DEFAULT_QUESTIONNAIRE_FREQUENCY = "weekly";

const FULLWIDTH_ASCII_START = 0xff01;
const FULLWIDTH_ASCII_END = 0xff5e;
const FULLWIDTH_ASCII_OFFSET = 0xfee0;
const DASH_VARIANTS = new Set([
  "\u2010",
  "\u2011",
  "\u2012",
  "\u2013",
  "\u2014",
  "\u2015",
  "\u2212",
  "\u30fc",
  "\uff0d",
  "\uff70",
]);
const SPACE_VARIANTS = new Set(["\u00a0", "\u2007", "\u202f", "\u3000"]);
const STUDY_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{0,62}[A-Z0-9]$/u;
const PARTICIPANT_ID_PATTERNS = {
  [PARTICIPANT_ID_FORMATS.alphanumeric]: /^[A-Z0-9-]+$/u,
  [PARTICIPANT_ID_FORMATS.numeric]: /^\d+$/u,
};

// Recording modes share the questionnaire cadence set and default frequency.
export const RECORDING_MODE_FREQUENCIES = QUESTIONNAIRE_FREQUENCIES;
export const DEFAULT_RECORDING_MODE_FREQUENCY = "weekly";
export const DEFAULT_RECORDING_MODE_OCCURRENCES_PER_PERIOD = 1;

export function normalizeKeyboardCompatibleCharacters(value) {
  if (value === undefined || value === null) {
    return "";
  }

  let normalized = "";

  for (const char of String(value)) {
    const codePoint = char.codePointAt(0);

    if (SPACE_VARIANTS.has(char)) {
      normalized += " ";
      continue;
    }

    if (DASH_VARIANTS.has(char)) {
      normalized += "-";
      continue;
    }

    if (
      codePoint !== undefined &&
      codePoint >= FULLWIDTH_ASCII_START &&
      codePoint <= FULLWIDTH_ASCII_END
    ) {
      normalized += String.fromCodePoint(codePoint - FULLWIDTH_ASCII_OFFSET);
      continue;
    }

    normalized += char;
  }

  return normalized;
}

function normalizeControlledIdentifier(value, { uppercase = false } = {}) {
  const normalized = normalizeKeyboardCompatibleCharacters(value).trim();
  return uppercase ? normalized.toUpperCase() : normalized;
}

export function normalizeStudyCodeInput(value) {
  return normalizeKeyboardCompatibleCharacters(value).toUpperCase();
}

export function stripNumericLeadingZeros(value) {
  return String(value || "").replace(/^0+(?=\d)/u, "");
}

export function normalizeOccurrencesPerPeriod(value) {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue < 1) {
    return DEFAULT_RECORDING_MODE_OCCURRENCES_PER_PERIOD;
  }

  return Math.floor(parsedValue);
}

// The default recording-mode assignments for a new study: the default modes,
// each optional and on the default cadence.
function defaultRecordingModeAssignments(modeCatalogue = RECORDING_MODES) {
  const availableModes = Array.isArray(modeCatalogue) ? modeCatalogue : [];
  const availableModeValues = new Set(
    availableModes.map((mode) => mode?.value).filter(Boolean),
  );
  const seedModes = DEFAULT_RECORDING_MODES.filter((mode) =>
    availableModeValues.has(mode),
  );
  const initialModes = seedModes.length
    ? seedModes
    : availableModes.slice(0, 1).map((mode) => mode.value);

  return initialModes.map((mode) => ({
    mode,
    required: false,
    frequency: DEFAULT_RECORDING_MODE_FREQUENCY,
    occurrencesPerPeriod: DEFAULT_RECORDING_MODE_OCCURRENCES_PER_PERIOD,
  }));
}

export function isRecordingModeEnabledForProductPresentationProfile(
  mode,
  productPresentationProfile,
) {
  const featureKey =
    PRODUCT_PRESENTATION_RECORDING_MODE_FEATURE_KEYS[
      String(mode || "").trim()
    ] || null;

  if (!featureKey) {
    return false;
  }

  if (
    !productPresentationProfile ||
    typeof productPresentationProfile !== "object"
  ) {
    return true;
  }

  return Boolean(productPresentationProfile?.userWebApp?.modes?.[featureKey]);
}

export function filterRecordingModeCatalogForProductPresentationProfile(
  modeCatalogue = RECORDING_MODES,
  productPresentationProfile,
) {
  const availableModes = Array.isArray(modeCatalogue) ? modeCatalogue : [];

  return availableModes.filter((mode) =>
    isRecordingModeEnabledForProductPresentationProfile(
      mode?.value,
      productPresentationProfile,
    ),
  );
}

export function sanitizeRecordingModesFormSelection(
  recordingModes,
  {
    productPresentationProfile,
    modeCatalogue = RECORDING_MODES,
    fallbackToDefaults = false,
  } = {},
) {
  const availableModes =
    filterRecordingModeCatalogForProductPresentationProfile(
      modeCatalogue,
      productPresentationProfile,
    );
  const availableModeValues = new Set(availableModes.map((mode) => mode.value));
  const byMode = new Map(
    (Array.isArray(recordingModes) ? recordingModes : [])
      .filter((entry) => availableModeValues.has(entry?.mode))
      .map((entry) => [
        entry.mode,
        {
          mode: entry.mode,
          required: entry.required === true,
          frequency: entry.frequency || DEFAULT_RECORDING_MODE_FREQUENCY,
          occurrencesPerPeriod: normalizeOccurrencesPerPeriod(
            entry.occurrencesPerPeriod,
          ),
        },
      ]),
  );

  const selectedModes = availableModes
    .filter((mode) => byMode.has(mode.value))
    .map((mode) => byMode.get(mode.value));

  if (selectedModes.length > 0 || !fallbackToDefaults) {
    return selectedModes;
  }

  return defaultRecordingModeAssignments(availableModes);
}

export function buildDefaultUserWebAppFeatureAccess(
  productPresentationProfile = null,
) {
  const hasProfile =
    Boolean(productPresentationProfile) &&
    typeof productPresentationProfile === "object";

  return STUDY_USER_WEB_APP_FEATURE_SECTIONS.reduce((access, section) => {
    access[section.sectionKey] = section.features.reduce(
      (sectionAccess, feature) => {
        sectionAccess[feature.featureKey] = hasProfile
          ? Boolean(
              productPresentationProfile?.userWebApp?.[section.sectionKey]?.[
                feature.featureKey
              ],
            )
          : false;
        return sectionAccess;
      },
      {},
    );
    return access;
  }, {});
}

export function resolveUserWebAppFeatureAvailability(
  productPresentationProfile,
  sectionKey,
  featureKey,
) {
  if (
    !productPresentationProfile ||
    typeof productPresentationProfile !== "object"
  ) {
    return true;
  }

  return Boolean(
    productPresentationProfile?.userWebApp?.[sectionKey]?.[featureKey],
  );
}

export function filterUserWebAppFeatureSectionsForProductPresentationProfile(
  productPresentationProfile,
  sections = STUDY_USER_WEB_APP_FEATURE_SECTIONS,
) {
  const candidateSections = Array.isArray(sections)
    ? sections
    : STUDY_USER_WEB_APP_FEATURE_SECTIONS;

  return candidateSections
    .map((section) => ({
      ...section,
      features: (Array.isArray(section?.features)
        ? section.features
        : []
      ).filter((feature) =>
        resolveUserWebAppFeatureAvailability(
          productPresentationProfile,
          section.sectionKey,
          feature.featureKey,
        ),
      ),
    }))
    .filter((section) => section.features.length > 0);
}

export function sanitizeUserWebAppFeatureAccess(
  featureAccess,
  { productPresentationProfile } = {},
) {
  const defaultAccess = buildDefaultUserWebAppFeatureAccess(
    productPresentationProfile,
  );

  return STUDY_USER_WEB_APP_FEATURE_SECTIONS.reduce((access, section) => {
    access[section.sectionKey] = section.features.reduce(
      (sectionAccess, feature) => {
        const available = resolveUserWebAppFeatureAvailability(
          productPresentationProfile,
          section.sectionKey,
          feature.featureKey,
        );
        const requestedValue =
          featureAccess?.[section.sectionKey]?.[feature.featureKey];

        sectionAccess[feature.featureKey] =
          available &&
          (typeof requestedValue === "boolean"
            ? requestedValue
            : defaultAccess?.[section.sectionKey]?.[feature.featureKey] ===
              true);
        return sectionAccess;
      },
      {},
    );
    return access;
  }, {});
}

export const EMPTY_CREATE_FORM = {
  code: "",
  label: "",
  organisationId: "",
  participantIdEntryMode: PARTICIPANT_ID_ENTRY_MODES.freeEntry,
  participantIdFormat: PARTICIPANT_ID_FORMATS.alphanumeric,
  participantIdAllowlistText: "",
  recordingModes: defaultRecordingModeAssignments(),
  questionnaireAssignments: [],
  reminderEmailsEnabled: true,
  userWebAppFeatureAccess: buildDefaultUserWebAppFeatureAccess(),
};

export const EMPTY_STUDY_SETTINGS_FORM = {
  status: "active",
  participantIdEntryMode: PARTICIPANT_ID_ENTRY_MODES.freeEntry,
  participantIdFormat: PARTICIPANT_ID_FORMATS.alphanumeric,
  participantIdAllowlistText: "",
  recordingModes: defaultRecordingModeAssignments(),
  questionnaireAssignments: [],
  reminderEmailsEnabled: true,
  userWebAppFeatureAccess: buildDefaultUserWebAppFeatureAccess(),
};

export function normalizeJoinSlug(value) {
  return normalizeControlledIdentifier(value, { uppercase: true });
}

export function normalizeStudyCode(value) {
  return normalizeControlledIdentifier(value, { uppercase: true });
}

export function normalizeParticipantId(
  value,
  format = PARTICIPANT_ID_FORMATS.alphanumeric,
) {
  const normalized = normalizeControlledIdentifier(value, { uppercase: true });

  if (format === PARTICIPANT_ID_FORMATS.numeric) {
    return stripNumericLeadingZeros(normalized);
  }

  return normalized;
}

export function parseParticipantAllowlist(
  value,
  format = PARTICIPANT_ID_FORMATS.alphanumeric,
) {
  return Array.from(
    new Set(
      String(value || "")
        .split(/[\n,\t，、]+/u)
        .map((entry) => normalizeParticipantId(entry, format))
        .filter(Boolean),
    ),
  );
}

export function validateStudyCode(value) {
  return STUDY_CODE_PATTERN.test(normalizeStudyCode(value));
}

export function validateParticipantIdValue(
  value,
  format = PARTICIPANT_ID_FORMATS.alphanumeric,
) {
  const pattern = PARTICIPANT_ID_PATTERNS[format];

  if (!pattern) {
    return false;
  }

  return pattern.test(normalizeParticipantId(value, format));
}

export function validateParticipantIdConfig(form) {
  if (
    !Object.values(PARTICIPANT_ID_ENTRY_MODES).includes(
      form?.participantIdEntryMode,
    )
  ) {
    return { code: "PARTICIPANT_ID_ENTRY_MODE_INVALID" };
  }

  if (
    !Object.values(PARTICIPANT_ID_FORMATS).includes(form?.participantIdFormat)
  ) {
    return { code: "PARTICIPANT_ID_FORMAT_INVALID" };
  }

  if (form.participantIdEntryMode !== PARTICIPANT_ID_ENTRY_MODES.allowlist) {
    return null;
  }

  const allowlist = parseParticipantAllowlist(
    form.participantIdAllowlistText,
    form.participantIdFormat,
  );

  if (allowlist.length === 0) {
    return { code: "PARTICIPANT_ID_ALLOWLIST_REQUIRED" };
  }

  const invalidValue = allowlist.find(
    (value) => !validateParticipantIdValue(value, form.participantIdFormat),
  );

  return invalidValue
    ? {
        code: "PARTICIPANT_ID_VALUE_INVALID",
        details: {
          format: form.participantIdFormat,
          value: invalidValue,
        },
      }
    : null;
}

export function buildParticipantIdPayload(form) {
  return {
    entryMode: form.participantIdEntryMode,
    format: form.participantIdFormat,
    allowlist:
      form.participantIdEntryMode === PARTICIPANT_ID_ENTRY_MODES.allowlist
        ? parseParticipantAllowlist(
            form.participantIdAllowlistText,
            form.participantIdFormat,
          )
        : [],
  };
}

export function buildRecordingModesPayload(
  form,
  { productPresentationProfile, modeCatalogue = RECORDING_MODES } = {},
) {
  const availableModes =
    filterRecordingModeCatalogForProductPresentationProfile(
      modeCatalogue,
      productPresentationProfile,
    );
  const byMode = new Map(
    sanitizeRecordingModesFormSelection(form.recordingModes, {
      productPresentationProfile,
      modeCatalogue,
      fallbackToDefaults: false,
    }).map((entry) => [entry.mode, entry]),
  );

  return {
    modes: availableModes
      .filter((mode) => byMode.has(mode.value))
      .map((mode) => {
        const entry = byMode.get(mode.value);

        return {
          mode: mode.value,
          required: entry.required === true,
          frequency: entry.frequency || DEFAULT_RECORDING_MODE_FREQUENCY,
          occurrencesPerPeriod: normalizeOccurrencesPerPeriod(
            entry.occurrencesPerPeriod,
          ),
        };
      }),
  };
}

export function buildUserWebAppFeatureAccessPayload(
  form,
  { productPresentationProfile } = {},
) {
  return sanitizeUserWebAppFeatureAccess(form.userWebAppFeatureAccess, {
    productPresentationProfile,
  });
}

export function buildReminderEmailConfigPayload(form) {
  return {
    enabled: form.reminderEmailsEnabled !== false,
  };
}

export function toRecordingModesArray(
  study,
  { productPresentationProfile, modeCatalogue = RECORDING_MODES } = {},
) {
  const config = study?.recordingModesConfig || {};
  const modes = Array.isArray(config.modes) ? config.modes : null;
  // Backward-compat: a record stored before recording modes carried a schedule.
  const legacy = Array.isArray(config.permittedModes)
    ? config.permittedModes
    : null;

  if (!modes && !legacy) {
    return sanitizeRecordingModesFormSelection([], {
      productPresentationProfile,
      modeCatalogue,
      fallbackToDefaults: true,
    });
  }

  const byMode = new Map();

  if (modes) {
    for (const entry of modes) {
      if (entry?.mode) {
        byMode.set(entry.mode, {
          mode: entry.mode,
          required: entry.required === true,
          frequency: entry.frequency || DEFAULT_RECORDING_MODE_FREQUENCY,
          occurrencesPerPeriod: normalizeOccurrencesPerPeriod(
            entry.occurrencesPerPeriod,
          ),
        });
      }
    }
  } else {
    for (const mode of legacy) {
      byMode.set(mode, {
        mode,
        required: false,
        frequency: DEFAULT_RECORDING_MODE_FREQUENCY,
        occurrencesPerPeriod: DEFAULT_RECORDING_MODE_OCCURRENCES_PER_PERIOD,
      });
    }
  }

  return sanitizeRecordingModesFormSelection(
    filterRecordingModeCatalogForProductPresentationProfile(
      modeCatalogue,
      productPresentationProfile,
    )
      .filter((mode) => byMode.has(mode.value))
      .map((mode) => byMode.get(mode.value)),
    {
      productPresentationProfile,
      modeCatalogue,
      fallbackToDefaults: false,
    },
  );
}

export function recordingModesForStudy(study) {
  const selected = new Set(
    toRecordingModesArray(study).map((entry) => entry.mode),
  );

  return RECORDING_MODES.filter((mode) => selected.has(mode.value));
}

export function buildQuestionnaireAssignmentsPayload(form) {
  const byId = new Map(
    (form.questionnaireAssignments || []).map((assignment) => [
      assignment.questionnaireId,
      assignment,
    ]),
  );

  return {
    assignments: QUESTIONNAIRES.filter((questionnaire) =>
      byId.has(questionnaire.value),
    ).map((questionnaire) => {
      const assignment = byId.get(questionnaire.value);

      return {
        questionnaireId: questionnaire.value,
        required: assignment.required === true,
        frequency: assignment.frequency || DEFAULT_QUESTIONNAIRE_FREQUENCY,
      };
    }),
  };
}

export function toQuestionnaireAssignmentsArray(study) {
  const assignments = study?.questionnaireAssignmentsConfig?.assignments;

  if (!Array.isArray(assignments)) {
    return [];
  }

  return QUESTIONNAIRES.filter((questionnaire) =>
    assignments.some(
      (assignment) => assignment.questionnaireId === questionnaire.value,
    ),
  ).map((questionnaire) => {
    const assignment = assignments.find(
      (entry) => entry.questionnaireId === questionnaire.value,
    );

    return {
      questionnaireId: questionnaire.value,
      required: assignment.required === true,
      frequency: assignment.frequency || DEFAULT_QUESTIONNAIRE_FREQUENCY,
    };
  });
}

export function toStudySettingsForm(
  study,
  { productPresentationProfile, modeCatalogue = RECORDING_MODES } = {},
) {
  const participantConfig = study?.participantIdConfig || {};

  return {
    status: study?.status || "active",
    participantIdEntryMode:
      participantConfig.entryMode || PARTICIPANT_ID_ENTRY_MODES.freeEntry,
    participantIdFormat:
      participantConfig.format || PARTICIPANT_ID_FORMATS.alphanumeric,
    participantIdAllowlistText: Array.isArray(participantConfig.allowlist)
      ? participantConfig.allowlist.join("\n")
      : "",
    recordingModes: toRecordingModesArray(study, {
      productPresentationProfile,
      modeCatalogue,
    }),
    questionnaireAssignments: toQuestionnaireAssignmentsArray(study),
    reminderEmailsEnabled: study?.reminderEmailConfig?.enabled !== false,
    userWebAppFeatureAccess: sanitizeUserWebAppFeatureAccess(
      study?.userWebAppFeatureAccess,
      { productPresentationProfile },
    ),
  };
}
